import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Layout } from '../components/Layout';
import { HammerIcon, SparklesIcon, ShieldCheckIcon, UsersIcon } from 'lucide-react';
export function AboutPage() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <Layout data-id="element-287">
      {/* Hero Section */}
      <section className="py-24 px-6 bg-alabaster border-b-3 border-charcoal" data-id="element-288">
        <div className="max-w-7xl mx-auto" data-id="element-289">
          <motion.div initial="hidden" animate="visible" variants={containerVariants} className="max-w-3xl" data-id="element-290">
            <motion.span variants={itemVariants} className="inline-block bg-espresso/10 text-espresso px-4 py-2 font-bold uppercase tracking-widest text-xs mb-6" data-id="element-291">
              Our Story
            </motion.span>
            <motion.h1 variants={itemVariants} className="text-5xl md:text-7xl font-bold leading-tight mb-8" data-id="element-292">
              Dependable Service. <br data-id="element-293" />
              <span className="italic font-serif font-normal text-espresso" data-id="element-294">
                Quality
              </span>{' '}
              <br data-id="element-295" />
              Craftsmanship.
            </motion.h1>
            <motion.p variants={itemVariants} className="text-xl text-charcoal/60 leading-relaxed" data-id="element-296">
              Glad you found us. At NK Home Improvement, we take pride in
              delivering results that exceed expectations. From consultation to
              final installation, our team works closely with every customer to
              create a kitchen space that feels fresh, stylish, and built to
              last.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-24 px-6 bg-white" data-id="element-297">
        <div className="max-w-7xl mx-auto" data-id="element-298">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-3 border-charcoal" data-id="element-299">
            {[{
            icon: HammerIcon,
            title: 'Precise Workmanship',
            desc: 'We deliver clean and consistent workmanship that makes your cabinets look brand new—not just refreshed, but transformed.'
          }, {
            icon: SparklesIcon,
            title: 'Zero-Mess Process',
            desc: 'We work in your home the way we would want someone to work in ours—with respect, cleanliness, and professionalism.'
          }, {
            icon: ShieldCheckIcon,
            title: 'Honest Pricing',
            desc: "You get clear explanations, honest recommendations, and transparent pricing. We don't upsell what you don't need."
          }, {
            icon: UsersIcon,
            title: 'Fast Completion',
            desc: 'Most projects are completed in just a day or two, giving you a full remodel look without the long delays.'
          }].map((value, idx) => <div key={idx} className={`p-10 flex flex-col gap-6 group hover:bg-charcoal transition-colors duration-500 ${idx !== 3 ? 'lg:border-r-3 border-charcoal' : ''} ${idx !== 0 ? 'border-t-3 lg:border-t-0 border-charcoal' : ''}`} data-id="element-300">
                <value.icon className="w-10 h-10 text-espresso group-hover:text-alabaster transition-colors" data-id="element-301" />
                <h3 className="text-xl font-bold group-hover:text-alabaster transition-colors" data-id="element-302">
                  {value.title}
                </h3>
                <p className="text-charcoal/60 group-hover:text-alabaster/60 transition-colors leading-relaxed" data-id="element-303">
                  {value.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-24 px-6 bg-alabaster border-y-3 border-charcoal overflow-hidden" data-id="element-304">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-305">
          <div className="relative" data-id="element-306">
            <img src="https://images.pexels.com/photos/534151/pexels-photo-534151.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Workshop" className="border-3 border-charcoal w-full h-[500px] object-cover" data-id="element-307" />
            <div className="absolute -bottom-8 -right-8 w-48 h-48 bg-espresso border-3 border-charcoal hidden md:flex items-center justify-center p-6 text-center" data-id="element-308">
              <p className="text-alabaster font-bold uppercase tracking-widest text-xs" data-id="element-309">
                Stunning Results
              </p>
            </div>
          </div>
          <div className="space-y-8" data-id="element-310">
            <h2 className="text-4xl font-bold leading-tight" data-id="element-311">
              Why Homeowners Choose NK Refacing
            </h2>
            <p className="text-charcoal/60 leading-relaxed" data-id="element-312">
              Your kitchen deserves more than a quick fix. We specialize in
              high-end cabinet refacing that gives you the look of a full
              remodel without overspending or tearing up your home. You save up
              to 50% compared to full replacement, with the same stunning
              results and far less cost.
            </p>
            <p className="text-charcoal/60 leading-relaxed italic" data-id="element-313">
              "No long delays. No unnecessary mess. No surprises. Just a smooth,
              efficient process from start to finish."
            </p>
            <div className="pt-4" data-id="element-314">
              <a href="/process" className="inline-block border-b-2 border-espresso text-espresso font-bold uppercase tracking-widest text-sm hover:pb-2 transition-all" data-id="element-315">
                Explore Our Process →
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
}